import { type ReactNode, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';
import { useModalFocus } from './useModalFocus';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Strip label, e.g. "Secure page · EkApp". */
  label: string;
  secure?: boolean;
  /** Next to the close button, e.g. a "3 added" pill. */
  aside?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * An in-chat page: slides up over the chat, keeps keyboard focus inside while open, closes on
 * Escape or a tap on the dimmed background, and gives focus back to what opened it.
 */
export function Sheet({ open, onClose, label, secure = false, aside, children, footer }: SheetProps) {
  const labelId = useId();
  const panel = useRef<HTMLDivElement>(null);
  useModalFocus(open, panel, onClose, '.sheet__body');

  if (!open) return null;
  return createPortal(
    <div className="overlay">
      <div className="overlay__scrim" onClick={onClose} aria-hidden="true" />
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={labelId} ref={panel}>
        <div className="sheet__handle" aria-hidden="true">
          <i />
        </div>
        <div className="sheet__strip">
          <span className={`sheet__label${secure ? ' sheet__label--secure' : ''}`} id={labelId}>
            <Icon name={secure ? 'lock' : 'file-text'} size={15} strokeWidth={2} />
            {label}
          </span>
          <span className="sheet__aside">
            {aside}
            <button type="button" className="iconbtn" aria-label="Close" onClick={onClose}>
              <Icon name="x" />
            </button>
          </span>
        </div>
        <div className="sheet__body">{children}</div>
        {footer ? <div className="sheet__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
